// src/utils/listColumnWidths.js
//
// Canonical column widths for the shared list-view chrome.
// Locations is the source of truth. Leases mirrors these values so
// the indicator column and LOCATION column align when the user
// toggles between the two list views.
//
// History:
//   #110 — initial values (52 / 220) for width parity
//   #111 — bumped to 72 / 260 for breathing room (12px dot sits
//           comfortably centred in 72px; location names have space)
//   #916 — reduced LOCATION width 260→210 so Region column sits visually
//           closer to Location values (~16–24 px gap after longest name)
//   #929 — LIST_COL_REGION_W = 80 added so both tables share the same
//           canonical Region column width (Locations' existing 80 is canonical)
//   #1022 — LIST_COL_STAGE_STATUS_W = 110 added so Stage (Locations) and
//            Status (Leases) share a single canonical width
//   #1024 — remaining bare numbers promoted to named constants; M&A column
//            is shared (same 90px width in both tables)
//   LOCATIONS-LIST-COLUMN-SPACING-2 — widened LOCATION to 260, metric columns
//            to 110 each; added LIST_COL_M_AND_A_LOC_W so all 10 columns are
//            fully width-controlled
//   LOCATIONS-LIST-FIT-HEADERS — reduced LOCATION 260→205 and shortened three
//            headers (People/Assign %/Util. %) so all 10 columns fit in viewport
//   HUB-LIST-COLUMN-FIX — metric columns (Sq Ft/Seats/People/Assign %/Util. %)
//            reduced 110→100 each (−50 px total) so the 11th Hub column (64 px)
//            fits within the 1280 px viewport budget without horizontal scrolling.
//            Table minWidth set to sum of all 11 columns (1141 px) in
//            LocationsListTable so overflowX:auto scrolls correctly at narrow
//            viewports instead of squishing Hub to invisibility.
//   LOCATIONS-LIST-COLUMN-ORDER — LOCATION widened 205→225; minWidth bumped
//            to 1161 in LocationsListTable to match.
//   LOCATIONS-LIST-FIT-HUB — Seats/People/Assign%/Util% 100→80 each (−80 px),
//            M&A 110→90 (−20 px); new column sum 1061 px; minWidth in
//            LocationsListTable updated to 1061; Hub column made sortable
//            (DESC default, hubs first).
//   LEASES-LIST-POLISH-2 — LIST_COL_RISK_SCORE_W 100→72 so the Leases list
//            Risk Score column matches the Locations list Score column width
//            (LIST_COL_INDICATOR_W = 72); Location (225 px) and Status (110 px)
//            already matched.

export const LIST_COL_INDICATOR_W    = 72;   // HEALTH (Locations) / URGENCY (Leases)
export const LIST_COL_LOCATION_W     = 225;  // LOCATION name column
export const LIST_COL_REGION_W       = 80;   // REGION column (both tables)
export const LIST_COL_STAGE_STATUS_W = 110;  // STAGE (Locations) / STATUS (Leases)

// Locations-only columns
export const LIST_COL_SQFT_W         = 100;  // Sq Ft
export const LIST_COL_SEATS_W        =  80;  // Seats
export const LIST_COL_HEADCOUNT_W    =  80;  // Headcount
export const LIST_COL_PEAK_W         =  80;  // Peak day (badge attendance)
export const LIST_COL_ASSIGNED_PCT_W =  80;  // Assigned %
export const LIST_COL_UTIL_PCT_W     =  80;  // Utilization % (avgUtilPct)
export const LIST_COL_SPACE_FIT_W    =  80;  // Space Fit score badge

// Leases-only columns (first 2 cols match Offices list col 1–2 for visual parity)
export const LIST_COL_LEASES_LOCATION_W   = 225;  // Office (col 1 — matches LIST_COL_LOCATION_W = 225)
export const LIST_COL_LEASES_RISK_SCORE_W =  72;  // Risk (col 2 — legacy alias; prefer LIST_COL_LEASES_HEALTH_W)
export const LIST_COL_LEASES_HEALTH_W     = 120;  // Health pill (col 3 — wide enough for "Winding down")
export const LIST_COL_LEASES_STATUS_W     = 120;  // Status (col 3 — matches OfficesPage 120px Stage column)
export const LIST_COL_LEASES_TRAILING_W   = 100;  // Shared width for the 6 trailing columns (even distribution)
export const LIST_COL_TYPE_W              = 140;  // Type
export const LIST_COL_EXPIRE_DATE_W       = 110;  // Expire Date
export const LIST_COL_RENT_W              = 150;  // 2026 Rent
export const LIST_COL_RISK_SCORE_W        =  72;  // Risk Score chip (matches LIST_COL_INDICATOR_W — LEASES-LIST-POLISH-2)
export const LIST_COL_NEXT_CD_W           = 130;  // Next Critical Date
export const LIST_COL_ANNUAL_RENT_W       = 110;  // Annual Rent (totalCost)
export const LIST_COL_PSF_W               =  80;  // $/sf
//   LEASES-LIST-RENEWAL-SQFT — 130 px fits "2×10yr" with padding; 90 px matches
//   LIST_COL_M_AND_A_W (same secondary-numeric budget) for Sq Ft integers.
export const LIST_COL_RENEWAL_OPTION_W    = 130;  // Renewal Option (count × term)
export const LIST_COL_SQFT_LEASE_W        =  90;  // Sq Ft (leased area)

// Locations M&A column — explicit width so all 11 columns are fully width-controlled
export const LIST_COL_M_AND_A_LOC_W  =  90;  // M&A (Locations)

// Hub indicator column — centered diamond for hub locations (non-sortable)
export const LIST_COL_HUB_W          = 64;   // Hub (Locations)

// Shared trailing column (same width in both tables)
export const LIST_COL_M_AND_A_W      = 90;   // M&A (Leases)

// LEASES-COLUMN-BLEED-HUB-INDICATOR-S1:
//   - LIST_COL_LEASES_HUB_W: hub diamond column, positioned 2nd (after Office),
//     matching LIST_COL_HUB_W's centered-diamond treatment from the Offices list.
//   - LIST_COL_LEASES_ANNUAL_RENT_W: Annual Rent needs its own width — the shared
//     LIST_COL_LEASES_TRAILING_W (100px) is too narrow for the longest value
//     ("$10,174,772 USD"), which was bleeding into the adjacent Expire Date
//     column. Sized to comfortably fit that string right-aligned with padding.
export const LIST_COL_LEASES_HUB_W          =  64;  // Hub (Leases) — matches LIST_COL_HUB_W
export const LIST_COL_LEASES_ANNUAL_RENT_W  = 150;  // Annual Rent (fits "$10,174,772 USD")

// LEASES-LIST-EXPIRE-BLEED-v2:
//   Expire Date needs its own (wider) width — the shared
//   LIST_COL_LEASES_TRAILING_W (100px) is too narrow for the longest
//   realistic string ("Dec 31, 2026 · 68mo ago"), which was bleeding into
//   the adjacent Annual Rent column. Sized to comfortably fit that string
//   plus a small right-gutter before the next column.
// LEASES-LIST-TABLE-POLISH: bumped 190→210 so "Dec 31 2026 · 3mo ago" is
//   never clipped; whiteSpace:nowrap on the cell ensures no mid-string wrap.
// LEASES-LIST-TIGHTEN: reduced 210→180 + cell padding from 36px→16px (0 8px);
//   combined saves 50px of column budget while keeping "Dec 31, 2026 · 68mo ago"
//   unclipped. whiteSpace:nowrap ensures no mid-string line break.
// LEASES-LIST-EXPIRE-COLUMN-WIDTH: restored 180→210; 180px clips longer expired
//   strings like "Sep 30, 2024 · 89mo ago" into the next column. 210px is the
//   confirmed minimum to fit the longest realistic value with 8px padding.
export const LIST_COL_LEASES_EXPIRE_DATE_W  = 210;  // Expire Date (fits "Sep 30, 2024 · 89mo ago" + 8px padding)
